import React, { ReactElement, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../store/types';
import useSessionStorage, { SessionStorageReservedKey } from '../hooks/global/useSessionStorage';
import AllergensToggleButton from '../components-library/menu/allergens/AllergensToggleButton';
import AllergensTip from '../components-library/menu/allergens/AllergensTip';
import NutritionInfo from '../components-library/menu/allergens/NutritionInfo';
import AllergensConfirmationModal from '../modals/allergens/AllergensConfirmationModal';

export enum AllergenVariant {
  Default = 'default',
  Hovered = 'hovered',
}

const HovererAllergensVariantStores = [693, 1142];

export interface AllergensContextInterface {
  variant: AllergenVariant;
  shouldShowCardAllergensTip: boolean;
  shouldShowModalAllergensTip: boolean;
  shouldShowAllergensConfirmationModal: boolean;
  isAllergensOpened: boolean;
  setIsAllergensOpened: (value: boolean) => void;
  toggleAllergensOpened: () => void;
  setCardTipSeen: () => void;
  setModalTipSeen: () => void;
  setShowAllergensConfirmationModal: (value: boolean) => void;
  setAllergensConfirmed: () => void;
  AllergensToggleButton: typeof AllergensToggleButton;
  AllergensTip: typeof AllergensTip;
  NutritionInfo: typeof NutritionInfo;
}

export const AllergensContext = React.createContext<AllergensContextInterface>(null);

export default function AllergensProvider({ children }: { children: ReactElement }) {
  const { company } = useSelector((store: StoreInterface) => store.initialData);

  const { getItem, setItem } = useSessionStorage();

  const [isAllergensOpened, setIsAllergensOpened] = useState(false);
  const toggleAllergensOpened = () => setIsAllergensOpened((current) => !current);

  const [cardTipSeen, _setCardTipSeen] = useState(Boolean(getItem(SessionStorageReservedKey.AllergensCardTipSeen)));
  const [modalTipSeen, _setModalTipSeen] = useState(Boolean(getItem(SessionStorageReservedKey.AllergensModalTipSeen)));

  const [showAllergensConfirmationModal, setShowAllergensConfirmationModal] = useState(false);
  const [allergensConfirmed, _setAllergensConfirmed] = useState(
    Boolean(getItem(SessionStorageReservedKey.AllergensModalConfirmed)),
  );

  const variant = useMemo<AllergenVariant>(
    () => (HovererAllergensVariantStores.includes(company?.id) ? AllergenVariant.Hovered : AllergenVariant.Default),
    [company?.id],
  );
  const shouldShowCardAllergensTip =
    variant === AllergenVariant.Hovered && !cardTipSeen && !getItem(SessionStorageReservedKey.AllergensCardTipSeen);
  const shouldShowModalAllergensTip =
    variant === AllergenVariant.Hovered && !modalTipSeen && !getItem(SessionStorageReservedKey.AllergensModalTipSeen);
  const shouldShowAllergensConfirmationModal =
    variant === AllergenVariant.Hovered &&
    !allergensConfirmed &&
    !getItem(SessionStorageReservedKey.AllergensModalConfirmed);

  const setCardTipSeen = () => {
    _setCardTipSeen(true);
    setItem(SessionStorageReservedKey.AllergensCardTipSeen, true);
  };
  const setModalTipSeen = () => {
    _setModalTipSeen(true);
    setItem(SessionStorageReservedKey.AllergensModalTipSeen, true);
  };
  const setAllergensConfirmed = () => {
    _setAllergensConfirmed(true);
    setItem(SessionStorageReservedKey.AllergensModalConfirmed, true);
  };

  const context = {
    variant,
    shouldShowCardAllergensTip,
    shouldShowModalAllergensTip,
    shouldShowAllergensConfirmationModal,
    isAllergensOpened,
    setIsAllergensOpened,
    toggleAllergensOpened,
    setCardTipSeen,
    setModalTipSeen,
    setShowAllergensConfirmationModal,
    setAllergensConfirmed,
    AllergensToggleButton,
    AllergensTip,
    NutritionInfo,
  };

  return (
    <AllergensContext.Provider value={context}>
      {children}
      {showAllergensConfirmationModal && shouldShowAllergensConfirmationModal && (
        <AllergensConfirmationModal open onClose={() => setShowAllergensConfirmationModal(false)} />
      )}
    </AllergensContext.Provider>
  );
}
